import { FEATURE_DIM, FEATURE_NAMES, featureVectorToArray, maskPitch, } from "./features.js";
export const DEFAULT_CLASSIFIER_CONFIG = {
    C: 2.0,
    gamma: 2.0,
    epochs: 200,
    discardPortion: 0.1,
    impulseCBoost: 6.0,
    impulseLabels: ["pop"],
    rejectConfidence: 0.45,
    maxImbalance: 4,
    absoluteCap: 300,
    testFraction: 0,
};
const SILENCE_LABEL = "silence";
export class NeuralClassifier {
    constructor(config = {}) {
        Object.defineProperty(this, "lastSampledInferLog", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastInferLabel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "lastInferLog", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "svms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "labels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "cfg", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "impulseSet", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pitchInvariantLabels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "anyPitchVariant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.cfg = { ...DEFAULT_CLASSIFIER_CONFIG, ...config };
        this.impulseSet = new Set(this.cfg.impulseLabels);
    }
    get ready() {
        return this.svms.length > 0 && this.labels.length > 0;
    }
    static rbf(a, b, gamma) {
        let d = 0;
        for (let i = 0; i < FEATURE_DIM; i++) {
            const diff = (a[i] ?? 0) - (b[i] ?? 0);
            d += diff * diff;
        }
        return Math.exp(-gamma * d);
    }
    static balanceCorpus(frames, labels, maxImbalance, absoluteCap) {
        const byLabel = new Map();
        for (const l of labels)
            byLabel.set(l, []);
        for (const f of frames)
            byLabel.get(f.label)?.push(f);
        let minCount = Infinity;
        for (const l of labels) {
            minCount = Math.min(minCount, byLabel.get(l).length);
        }
        if (!Number.isFinite(minCount) || minCount === 0) {
            const empty = labels.filter((l) => byLabel.get(l).length === 0);
            if (empty.length > 0) {
                console.warn("[classifier] balanceCorpus skipped: label(s) with 0 rows " +
                    `[${empty.join(",")}] — capture data for these before training`);
            }
            return frames;
        }
        const cap = Math.min(Math.max(minCount * maxImbalance, minCount), absoluteCap);
        const out = [];
        for (const l of labels) {
            const rows = byLabel.get(l);
            if (rows.length <= cap) {
                out.push(...rows);
                continue;
            }
            const shuffled = [...rows];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            out.push(...shuffled.slice(0, cap));
            console.info(`[classifier] balanced '${l}' ${rows.length}→${cap} ` +
                `(min class=${minCount}, max imbalance ${maxImbalance}:1)`);
        }
        return out;
    }
    getConfig() {
        return { ...this.cfg, impulseLabels: [...this.cfg.impulseLabels] };
    }
    configure(patch) {
        this.cfg = { ...this.cfg, ...patch };
        if (patch.impulseLabels) {
            this.impulseSet = new Set(this.cfg.impulseLabels);
        }
        console.info("[classifier] config updated", { ...this.cfg });
    }
    getLabels() {
        return [...this.labels];
    }
    setPitchInvariantLabels(labels) {
        this.pitchInvariantLabels = new Set(labels);
    }
    async train(frames, _opts = {}) {
        const labels = [...new Set(frames.map((f) => f.label))].sort();
        if (labels.length < 2) {
            throw new Error("need at least 2 labels to train a classifier");
        }
        this.anyPitchVariant = labels.some((l) => !this.pitchInvariantLabels.has(l));
        frames = frames.map((f) => this.pitchInvariantLabels.has(f.label)
            ? { label: f.label, features: maskPitch(f.features) }
            : f);
        console.info("[classifier] pitch policy: " +
            (this.anyPitchVariant
                ? `pitch-variant labels=[${labels
                    .filter((l) => !this.pitchInvariantLabels.has(l))
                    .join(",") || "none"}]`
                : "ALL labels pitch-invariant (pitch removed)"));
        frames = NeuralClassifier.balanceCorpus(frames, labels, this.cfg.maxImbalance, this.cfg.absoluteCap);
        this.logCorpusStats(frames, labels);
        const shuffled = [...frames];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        const testFrac = this.cfg.testFraction;
        const testSet = [];
        const trainSet = [];
        for (const label of labels) {
            const rows = shuffled.filter((f) => f.label === label);
            const nTest = Math.max(1, Math.floor(rows.length * testFrac));
            const takeTest = Math.min(nTest, Math.max(0, rows.length - 1));
            for (let i = 0; i < rows.length; i++) {
                if (i < takeTest)
                    testSet.push(rows[i]);
                else
                    trainSet.push(rows[i]);
            }
        }
        console.info(`[classifier] SVM split total=${frames.length} ` +
            `train=${trainSet.length} test=${testSet.length} ` +
            `(≈${Math.round(testFrac * 100)}% held out, stratified)`);
        const X = trainSet.map((f) => f.features);
        const n = X.length;
        const K = new Array(n);
        for (let i = 0; i < n; i++) {
            K[i] = new Array(n);
        }
        const gamma = this.cfg.gamma;
        for (let i = 0; i < n; i++) {
            K[i][i] = NeuralClassifier.rbf(X[i], X[i], gamma);
            for (let j = i + 1; j < n; j++) {
                const k = NeuralClassifier.rbf(X[i], X[j], gamma);
                K[i][j] = k;
                K[j][i] = k;
            }
        }
        this.svms = [];
        for (const label of labels) {
            const y = trainSet.map((f) => (f.label === label ? 1 : -1));
            const cPos = this.impulseSet.has(label)
                ? this.cfg.C * this.cfg.impulseCBoost
                : this.cfg.C;
            if (cPos !== this.cfg.C) {
                console.info(`[classifier] class-weighted C for impulse '${label}': ` +
                    `${this.cfg.C}→${cPos} (under-represented positive class)`);
            }
            const svm = this.trainBinary(K, X, y, cPos);
            svm.pitchInvariant = this.pitchInvariantLabels.has(label);
            this.svms.push(svm);
            await new Promise((r) => setTimeout(r, 0));
        }
        this.labels = labels;
        const finalAcc = this.evaluateAccuracy(trainSet, labels);
        const testAcc = this.evaluateAccuracy(testSet, labels);
        const totalSv = this.svms.reduce((a, s) => a + s.sv.length, 0);
        console.info(`[classifier] SVM trained labels=${labels.join(",")} ` +
            `supportVectors=${totalSv} trainAcc=${finalAcc.toFixed(3)} ` +
            `testAcc=${testAcc.toFixed(3)}`);
        this.logSampledIO("train", trainSet, labels);
        this.logSampledIO("test", testSet, labels);
        if (testSet.length > 0 && finalAcc - testAcc > 0.25) {
            console.warn("[classifier] large train≫test gap — classes may overlap " +
                "or gamma is too high (overfitting).");
        }
        return {
            labels,
            epochs: this.cfg.epochs,
            finalLoss: 0,
            finalAcc,
            frames: trainSet.length,
            testAcc,
        };
    }
    classify(f) {
        if (!this.ready)
            return { label: null, confidence: 0, scores: {} };
        if (this.svms.length !== this.labels.length) {
            console.warn(`[classifier] svm/label count mismatch ` +
                `(svms=${this.svms.length} labels=${this.labels.length}); ` +
                "skipping classification — retrain the model.");
            return { label: null, confidence: 0, scores: {} };
        }
        const arr = featureVectorToArray(f);
        const maskedArr = maskPitch(arr);
        const silenceIdx = this.labels.indexOf(SILENCE_LABEL);
        if (silenceIdx >= 0 && this.svms[silenceIdx]) {
            const svm = this.svms[silenceIdx];
            const silDecision = this.decision(svm, svm.pitchInvariant ? maskedArr : arr);
            if (silDecision >= 0) {
                const scores = {};
                for (const l of this.labels)
                    scores[l] = 0;
                scores[SILENCE_LABEL] = 1;
                this.maybeSampledInferLog(this.anyPitchVariant ? arr : maskedArr, this.labels, scores);
                return { label: null, confidence: 0, scores };
            }
        }
        const decisions = this.svms.map((svm, i) => svm && i !== silenceIdx
            ? this.decision(svm, svm.pitchInvariant ? maskedArr : arr)
            : -Infinity);
        const nonsilence = [];
        for (let i = 0; i < this.labels.length; i++) {
            if (i === silenceIdx)
                continue;
            nonsilence.push(i);
        }
        const impulseGroup = nonsilence.filter((i) => this.impulseSet.has(this.labels[i]));
        const sustainGroup = nonsilence.filter((i) => !this.impulseSet.has(this.labels[i]));
        const bestOf = (group) => {
            let b = -Infinity;
            for (const i of group)
                b = Math.max(b, decisions[i]);
            return b;
        };
        const impulseBest = bestOf(impulseGroup);
        const sustainBest = bestOf(sustainGroup);
        let activeGroup;
        if (impulseGroup.length > 0 && sustainGroup.length > 0) {
            activeGroup = impulseBest >= sustainBest ? impulseGroup : sustainGroup;
        }
        else {
            activeGroup = nonsilence;
        }
        let maxD = -Infinity;
        for (const i of activeGroup)
            maxD = Math.max(maxD, decisions[i]);
        let denom = 0;
        const exps = new Array(this.labels.length).fill(0);
        for (const i of activeGroup) {
            const e = Math.exp(decisions[i] - maxD);
            exps[i] = e;
            denom += e;
        }
        const scores = {};
        for (const l of this.labels)
            scores[l] = 0;
        let best = -1;
        let bestV = -Infinity;
        for (const i of activeGroup) {
            const p = exps[i] / (denom || 1);
            scores[this.labels[i]] = p;
            if (p > bestV) {
                bestV = p;
                best = i;
            }
        }
        const topDecision = best >= 0 ? decisions[best] : -Infinity;
        const rejected = best < 0 || bestV < this.cfg.rejectConfidence || topDecision < 0;
        const label = rejected ? null : this.labels[best];
        this.maybeLogInference(this.labels, exps, denom);
        this.maybeSampledInferLog(this.anyPitchVariant ? arr : maskedArr, this.labels, scores);
        return {
            label,
            confidence: rejected ? 0 : bestV,
            scores,
        };
    }
    async serialize() {
        if (!this.ready)
            return null;
        return {
            version: 2,
            labels: [...this.labels],
            featureNames: [...FEATURE_NAMES],
            modelTopology: {
                kind: "rbf-svm",
                gamma: this.cfg.gamma,
                config: this.getConfig(),
                svms: this.svms,
                pitchInvariantLabels: [...this.pitchInvariantLabels],
                anyPitchVariant: this.anyPitchVariant,
            },
            weightSpecs: null,
            weightDataB64: "",
        };
    }
    /** Restore SVMs from serialized form. */
    async load(s) {
        const topo = s.modelTopology;
        if (!topo || topo.kind !== "rbf-svm" || !Array.isArray(topo.svms)) {
            console.warn("[classifier] persisted model is not an rbf-svm; ignoring " +
                "(recapture sounds to train a fresh SVM)");
            this.svms = [];
            this.labels = [];
            return;
        }
        if (topo.config) {
            this.cfg = { ...DEFAULT_CLASSIFIER_CONFIG, ...topo.config };
        }
        else if (typeof topo.gamma === "number") {
            this.cfg = { ...this.cfg, gamma: topo.gamma };
        }
        this.impulseSet = new Set(this.cfg.impulseLabels);
        this.pitchInvariantLabels = new Set(topo.pitchInvariantLabels ?? []);
        this.anyPitchVariant = topo.anyPitchVariant ?? true;
        this.svms = topo.svms.map((m) => ({
            sv: m.sv.map((r) => r.slice(0, FEATURE_DIM)),
            coef: [...m.coef],
            b: m.b,
            pitchInvariant: m.pitchInvariant,
        }));
        this.labels = [...s.labels];
        console.info(`[classifier] SVM restored labels=${this.labels.join(",")} ` +
            `supportVectors=${this.svms.reduce((a, m) => a + m.sv.length, 0)} ` +
            `gamma=${this.cfg.gamma}`);
    }
    maybeLogInference(labels, exps, denom) {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        let best = -1;
        let bestV = -Infinity;
        for (let i = 0; i < labels.length; i++) {
            const v = exps[i] / (denom || 1);
            if (v > bestV) {
                bestV = v;
                best = i;
            }
        }
        const winner = best >= 0 ? labels[best] : null;
        const changed = winner !== this.lastInferLabel;
        this.lastInferLabel = winner;
        if (!changed &&
            now - this.lastInferLog < NeuralClassifier.INFER_LOG_INTERVAL_MS) {
            return;
        }
        this.lastInferLog = now;
        const dist = labels
            .map((l, i) => `${l}=${(exps[i] / (denom || 1)).toFixed(2)}`)
            .join(" ");
        console.debug(`[classifier] infer winner=${winner ?? "none"} [${dist}]`);
    }
    maybeSampledInferLog(arr, labels, scores) {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        if (now - this.lastSampledInferLog <
            NeuralClassifier.SAMPLED_INFER_INTERVAL_MS) {
            return;
        }
        this.lastSampledInferLog = now;
        let winner = "none";
        let bestV = -Infinity;
        for (const l of labels) {
            if ((scores[l] ?? 0) > bestV) {
                bestV = scores[l] ?? 0;
                winner = l;
            }
        }
        const feat = FEATURE_NAMES.map((n, i) => `${n}=${(arr[i] ?? 0).toFixed(3)}`).join(" ");
        const dist = labels
            .map((l) => `${l}=${(scores[l] ?? 0).toFixed(3)}`)
            .join(" ");
        const silTxt = "silence" in scores
            ? ` silenceP=${(scores["silence"] ?? 0).toFixed(3)}`
            : " (no 'silence' class trained)";
        const energyIdx = FEATURE_NAMES.indexOf("energy");
        const energyVal = energyIdx >= 0 ? (arr[energyIdx] ?? 0) : NaN;
        console.debug(`[classifier] SAMPLED-INFER (5s) win=${winner}${silTxt} ` +
            `liveEnergy=${Number.isFinite(energyVal) ? energyVal.toFixed(3) : "n/a"}` +
            `\n    in[ ${feat} ]\n    scores[ ${dist} ]`);
    }
    trainBinary(K, X, y, cPos = this.cfg.C) {
        const n = X.length;
        const alpha = new Float64Array(n);
        const cBase = this.cfg.C;
        const cOf = (i) => (y[i] > 0 ? cPos : cBase);
        for (let epoch = 0; epoch < this.cfg.epochs; epoch++) {
            for (let i = 0; i < n; i++) {
                let sum = 0;
                for (let j = 0; j < n; j++) {
                    if (alpha[j] === 0)
                        continue;
                    sum += alpha[j] * y[j] * K[i][j];
                }
                const grad = 1 - y[i] * sum;
                const Kii = K[i][i] || 1e-6;
                let a = alpha[i] + grad / Kii;
                const Ci = cOf(i);
                if (a < 0)
                    a = 0;
                else if (a > Ci)
                    a = Ci;
                alpha[i] = a;
            }
        }
        const eps = 1e-6;
        const idx = [];
        for (let i = 0; i < n; i++)
            if (alpha[i] > eps)
                idx.push(i);
        idx.sort((p, q) => alpha[p] - alpha[q]);
        const drop = Math.floor(idx.length * this.cfg.discardPortion);
        const kept = idx.slice(drop);
        const keptIdx = kept.length >= 2 ? kept : idx;
        const sv = [];
        const coef = [];
        for (const i of keptIdx) {
            sv.push(X[i].slice(0, FEATURE_DIM));
            coef.push(alpha[i] * y[i]);
        }
        let bSum = 0;
        let bCount = 0;
        const gamma = this.cfg.gamma;
        for (const i of keptIdx) {
            const Ci = cOf(i);
            if (alpha[i] <= eps || alpha[i] >= Ci - eps)
                continue;
            let f = 0;
            for (let s = 0; s < sv.length; s++) {
                f += coef[s] * NeuralClassifier.rbf(sv[s], X[i], gamma);
            }
            bSum += y[i] - f;
            bCount++;
        }
        const b = bCount > 0 ? bSum / bCount : 0;
        return { sv, coef, b };
    }
    decision(svm, x) {
        let f = svm.b;
        const gamma = this.cfg.gamma;
        for (let s = 0; s < svm.sv.length; s++) {
            f += svm.coef[s] * NeuralClassifier.rbf(svm.sv[s], x, gamma);
        }
        return f;
    }
    evaluateAccuracy(frames, labels) {
        if (!this.ready || frames.length === 0)
            return 0;
        let correct = 0;
        for (const fr of frames) {
            let best = -1;
            let bestV = -Infinity;
            for (let i = 0; i < this.svms.length; i++) {
                const d = this.decision(this.svms[i], fr.features);
                if (d > bestV) {
                    bestV = d;
                    best = i;
                }
            }
            if (best >= 0 && labels[best] === fr.label)
                correct++;
        }
        return correct / frames.length;
    }
    logCorpusStats(frames, labels) {
        const meansByLabel = {};
        const stdsByLabel = {};
        for (const label of labels) {
            const rows = frames.filter((f) => f.label === label);
            if (rows.length === 0)
                continue;
            const parts = [];
            const means = [];
            const stds = [];
            const saturated = [];
            for (let d = 0; d < FEATURE_DIM; d++) {
                let sum = 0;
                let min = Infinity;
                let max = -Infinity;
                for (const r of rows) {
                    const v = r.features[d] ?? 0;
                    sum += v;
                    if (v < min)
                        min = v;
                    if (v > max)
                        max = v;
                }
                const mean = sum / rows.length;
                let varAcc = 0;
                for (const r of rows) {
                    const v = (r.features[d] ?? 0) - mean;
                    varAcc += v * v;
                }
                const std = Math.sqrt(varAcc / rows.length);
                means[d] = mean;
                stds[d] = std;
                const pinnedHigh = mean > 0.97 && std < 0.02;
                const pinnedLow = mean < 0.03 && std < 0.02;
                if (pinnedHigh || pinnedLow) {
                    saturated.push(`${FEATURE_NAMES[d]}(≈${mean.toFixed(2)})`);
                }
                parts.push(`${FEATURE_NAMES[d]}: μ=${mean.toFixed(3)} ` +
                    `σ=${std.toFixed(3)} [${min.toFixed(2)}..${max.toFixed(2)}]`);
            }
            meansByLabel[label] = means;
            stdsByLabel[label] = stds;
            console.info(`[classifier] corpus '${label}' n=${rows.length}\n    ` +
                parts.join("\n    "));
            if (saturated.length > 0) {
                console.warn(`[classifier] ⚠ label '${label}' has SATURATED/DEAD ` +
                    `feature(s): ${saturated.join(", ")}. Lower "Loudness gain" ` +
                    `until loud sounds read ~0.6..0.9, then RECAPTURE.`);
            }
        }
        this.logSeparabilityMatrix(labels, meansByLabel, stdsByLabel);
        this.logSilenceDiagnostic(labels, meansByLabel, stdsByLabel);
    }
    logSilenceDiagnostic(labels, means, stds) {
        if (!labels.includes("silence")) {
            console.info("[classifier] silence-diagnostic: no 'silence' label — " +
                "the SVM has no explicit reject class, but the inference " +
                "reject margin still discards low-confidence frames.");
            return;
        }
        const energyIdx = FEATURE_NAMES.indexOf("energy");
        if (energyIdx < 0)
            return;
        const silMean = means["silence"]?.[energyIdx] ?? 0;
        const silStd = stds["silence"]?.[energyIdx] ?? 0;
        const voiced = labels.filter((l) => l !== "silence");
        const voicedEnergies = voiced.map((l) => means[l]?.[energyIdx] ?? 0);
        const voxMean = voicedEnergies.length > 0
            ? voicedEnergies.reduce((a, b) => a + b, 0) / voicedEnergies.length
            : 0;
        const gap = voxMean - silMean;
        const saturatedVoiced = voiced.filter((l) => (means[l]?.[energyIdx] ?? 0) > 0.97 &&
            (stds[l]?.[energyIdx] ?? 1) < 0.02);
        console.info("[classifier] silence-diagnostic: " +
            `silenceEnergy μ=${silMean.toFixed(3)} σ=${silStd.toFixed(3)}; ` +
            `voicedEnergy μ=${voxMean.toFixed(3)}; gap=${gap.toFixed(3)}; ` +
            `saturatedVoicedLabels=[${saturatedVoiced.join(",") || "none"}]`);
        if (saturatedVoiced.length > 0) {
            console.error("[classifier] ⚠ energy is a DEAD feature for voiced labels " +
                `[${saturatedVoiced.join(",")}]. Lower "Loudness gain" and ` +
                "recapture every sound.");
        }
    }
    logSeparabilityMatrix(labels, means, stds) {
        const eps = 1e-3;
        const lines = [];
        for (let i = 0; i < labels.length; i++) {
            for (let j = i + 1; j < labels.length; j++) {
                const a = labels[i];
                const b = labels[j];
                const ma = means[a];
                const mb = means[b];
                const sa = stds[a];
                const sb = stds[b];
                if (!ma || !mb)
                    continue;
                let dist = 0;
                const perDim = [];
                for (let d = 0; d < FEATURE_DIM; d++) {
                    const dd = Math.abs(ma[d] - mb[d]) / ((sa?.[d] ?? 0) + (sb?.[d] ?? 0) + eps);
                    dist += dd;
                    perDim.push(`${FEATURE_NAMES[d]}=${dd.toFixed(1)}`);
                }
                const weak = dist < labels.length;
                lines.push(`  ${a} ↔ ${b}: separability=${dist.toFixed(1)}` +
                    (weak ? "  ⚠ WEAK" : "") +
                    `  [${perDim.join(" ")}]`);
            }
        }
        console.info("[classifier] class separability (higher = more distinct; " +
            "⚠ WEAK pairs overlap and may be confused):\n" +
            lines.join("\n"));
    }
    logSampledIO(partition, frames, labels) {
        if (!this.ready || frames.length === 0)
            return;
        const lines = [];
        for (const label of labels) {
            const rows = frames.filter((f) => f.label === label);
            if (rows.length === 0)
                continue;
            const pick = rows[Math.floor(Math.random() * rows.length)];
            const decisions = this.svms.map((svm) => this.decision(svm, pick.features));
            let best = -1;
            let bestV = -Infinity;
            for (let i = 0; i < labels.length; i++) {
                if ((decisions[i] ?? -Infinity) > bestV) {
                    bestV = decisions[i];
                    best = i;
                }
            }
            const predicted = best >= 0 ? labels[best] : "none";
            const correct = predicted === label ? "✓" : "✗ MISPREDICT";
            const feat = FEATURE_NAMES.map((n, i) => `${n}=${(pick.features[i] ?? 0).toFixed(3)}`).join(" ");
            const dist = labels
                .map((l, i) => `${l}=${(decisions[i] ?? 0).toFixed(3)}`)
                .join(" ");
            lines.push(`  '${label}' → '${predicted}' ${correct}\n` +
                `      in[ ${feat} ]\n` +
                `      decision[ ${dist} ]`);
        }
        console.info(`[classifier] sampled ${partition.toUpperCase()} I/O ` +
            `(one random example per label):\n` +
            lines.join("\n"));
    }
}
Object.defineProperty(NeuralClassifier, "SAMPLED_INFER_INTERVAL_MS", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 5000
});
Object.defineProperty(NeuralClassifier, "INFER_LOG_INTERVAL_MS", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 5000
});
